import { useCallback } from 'react';
import { GameItemResponse } from '../../components/Games/games.model';
import { useFetch } from '../fetch/useFetch';

interface RecentlyPlayedResponse {
  games: GameItemResponse[];
}

interface UseFetchRecentlyPlayedGamesParams {
  userId: string;
  accessToken: string;
}

/**
 * Hook to fetch recently played games for a user
 * Makes a GET request to /recentlyPlayed endpoint with userId query param
 * Requires Authorization header with Bearer token
 * Returns up to 6 ordered GameItemResponse items
 */
export const useFetchRecentlyPlayedGames = ({
  userId,
  accessToken,
}: UseFetchRecentlyPlayedGamesParams): ReturnType<typeof useFetch<RecentlyPlayedResponse>> => {
  const dataRequest = useCallback((): Promise<RecentlyPlayedResponse> => {
    const url = `/api/recentlyPlayed?userId=${encodeURIComponent(userId)}`;

    return fetch(url, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    }).then((response) => {
      if (!response.ok) {
        throw new Error(`Failed to fetch recently played games: ${response.statusText}`);
      }

      return response.json() as Promise<RecentlyPlayedResponse>;
    });
  }, [userId, accessToken]);

  return useFetch<RecentlyPlayedResponse>({ dataRequest });
};
